(() => {
  if (customElements.get('ving-nirun-assembly')) return;
  const clamp = n => Math.max(0, Math.min(1, n));
  const ease = n => { n = clamp(n); return n * n * (3 - 2 * n); };
  customElements.define('ving-nirun-assembly', class extends HTMLElement {
    connectedCallback() {
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.update = this.update.bind(this);
      this.queue = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      const options = { passive: true, signal: this.abort.signal };
      addEventListener('scroll', this.queue, options);
      addEventListener('resize', this.queue, options);
      this.motion.addEventListener('change', this.queue, options);
      this.querySelectorAll('img').forEach(image => image.addEventListener('load', this.queue, options));

      const sourcePhoto = this.querySelector('.nirun-assembly__real').src;
      const sourceAtlas = this.querySelector('[data-part]').src;
      this.querySelectorAll('[data-part]').forEach((part, i) => {
        if (i !== 2) part.src = i===1?new URL('ving-nirun-foot-interior.png',sourceAtlas).href:sourcePhoto;
      });
      const addSurface = (name, parent, src) => {
        const image = document.createElement('img');
        image.className = 'nirun-assembly__surface ' + name;
        image.dataset.follow = parent; image.src = src; image.alt = '';
        this.querySelector('.nirun-assembly__layers').append(image);
        image.addEventListener('load', this.queue, options);
      };
      addSurface('nirun-assembly__rim', '3', sourcePhoto);
      // Hidden construction surfaces use the existing illustration; the visible
      // assembled shell stays registered to the original product photograph.

      [['base-fill','3'],['rubber-fill','4']].forEach(([name,index]) => {
        const shell=document.createElement('span');
        shell.className='nirun-assembly__surface nirun-assembly__'+name;
        shell.dataset.follow=index;
        const image=document.createElement('img'); image.src=name==='foot-fill'?new URL('ving-nirun-foot-interior.png',sourceAtlas).href:name==='base-fill'?new URL('ving-nirun-base-interior.png',sourceAtlas).href:sourceAtlas; image.alt='';
        shell.append(image); this.querySelector('.nirun-assembly__layers').append(shell);
      });
      [['carbon-edge','2'],['rubber-edge','4']].forEach(([name,index])=>{
        const edge=document.createElement('span');
        edge.className='nirun-assembly__surface nirun-assembly__edge nirun-assembly__'+name;
        edge.dataset.follow=index; edge.setAttribute('aria-hidden','true');
        this.querySelector('.nirun-assembly__layers').append(edge);
      });
      // Paint inside the same cutout. A filter on a masked image is itself
      // clipped by the mask, so an outer drop shadow alone is not visible.
      [['upper','0'],['foot','1'],['carbon','2'],['base','3'],['rim','3'],['rubber','4']].forEach(([name,index])=>{
        const tint=document.createElement('span');
        tint.className='nirun-assembly__surface nirun-assembly__tint nirun-assembly__tint-'+name;
        tint.dataset.follow=index; tint.setAttribute('aria-hidden','true');
        if(name==='rubber') tint.append(document.createElement('span'));
        this.querySelector('.nirun-assembly__layers').append(tint);
      });

      const base = this.querySelector('.nirun-assembly__real');
      if (!this.querySelector('.nirun-assembly__focus')) {
        const focus = base.cloneNode();
        focus.className = 'nirun-assembly__focus';
        base.after(focus);
      }
      const dialog = this.querySelector('dialog');
      this.setHighlight = index => {
        this.highlight = index;
        this.querySelectorAll('[data-detail]').forEach(button => button.classList.toggle('is-highlighted', button.dataset.detail === index));
        this.queue();
      };
      this.querySelectorAll('[data-detail]').forEach(button => {
        button.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.setHighlight(button.dataset.detail); }, { signal: this.abort.signal });
        button.addEventListener('pointerleave', () => this.setHighlight(null), { signal: this.abort.signal });
        button.addEventListener('focus', () => this.setHighlight(button.dataset.detail), { signal: this.abort.signal });
        button.addEventListener('blur', () => this.setHighlight(null), { signal: this.abort.signal });
      });
      this.querySelectorAll('[data-detail]').forEach(button => button.addEventListener('click', () => {
        const copy = this.querySelector(`[data-copy="${button.dataset.detail}"]`);
        dialog.querySelector('[data-detail-number]').textContent = String(Number(button.dataset.detail) + 1).padStart(2, '0');
        dialog.querySelector('[data-detail-title]').textContent = copy.dataset.title;
        dialog.querySelector('[data-detail-copy]').textContent = copy.content.textContent;
        dialog.showModal();
      }, { signal: this.abort.signal }));
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }, { signal: this.abort.signal });
      this.queue();
    }
    disconnectedCallback() {
      this.querySelector('dialog')?.close();
      this.abort.abort(); cancelAnimationFrame(this.frame);
      if (this.target) this.target.style.opacity = '';
    }
    update() {
      this.frame = 0;
      const handoff = this.querySelector('.nirun-assembly__handoff');
      const layers = this.querySelector('.nirun-assembly__layers');
      const real = this.querySelector('.nirun-assembly__real');
      const focus = this.querySelector('.nirun-assembly__focus');
      const parts = [...this.querySelectorAll('[data-part]')];
      const loaded = real.complete && real.naturalWidth > 0 && parts.every(image => image.complete && image.naturalWidth > 0);
      const animated = !this.motion.matches && this.dataset.enabled !== 'false' && loaded;
      this.classList.toggle('is-animated', animated);
      const visual = this.querySelector('.nirun-assembly__visual');
      const stage = this.querySelector('.nirun-assembly__stage');
      const header = document.querySelector('.site-header');
      const top = Math.min(140, header ? header.getBoundingClientRect().height : 100);
      this.style.setProperty('--assembly-top', `${top}px`);
      const rect = this.getBoundingClientRect();
      // Preserve the assembly speed, then give the completed shoe its own
      // scroll distance before the existing handoff begins. CSS adds exactly
      // this distance to the pinned section, so the hold stays on screen.
      const hold = innerHeight * (parseFloat(getComputedStyle(this).getPropertyValue('--assembly-hold')) || 0);
      const travelDistance = Math.max(1, this.offsetHeight - innerHeight * .32 - hold);
      const distance = top - rect.top;
      const heldDistance = Math.max(0, Math.min(hold, distance - travelDistance * .84));
      const p = animated ? clamp((distance - heldDistance) / travelDistance) : 1;
      const W = visual.offsetWidth;
      const inspecting = this.highlight != null;
      const startScale = Math.min(.78, stage.clientHeight * .80 / (W * 1.35));
      const zoom = startScale + (1 - startScale) * ease((p-.30)/.40);
      // First close the outsole from BELOW the lower foam. Then cover the
      // carbon with the footbed; move this pair into the lower foam only after
      // that cover is complete. A common final lift cannot invert layer order.
      const rubberJoin = ease((p-.06)/.22);
      const cover = ease((p-.14)/.24);
      const seat = ease((p-.44)/.22);
      const upperJoin = ease((p-.66)/.18);
      const pack = .18 * (1-ease((p-.70)/.14));
      const carbon = pack - .14*(1-seat);
      const foot = carbon - .18*(1-cover);
      const offset = [foot-.22*(1-upperJoin),foot,carbon,pack,pack+.26*(1-rubberJoin)];
      parts.forEach((part,i) => {
        const y = animated ? offset[i]*W*zoom : 0;
        const transform = 'translateY('+y+'px) scale('+zoom+')';
        part.style.transform=transform;
        const selected = inspecting && String(i) === this.highlight;
        part.classList.toggle('is-highlighted',selected);
        // Keep physical depth order even while highlighting an assembled part.
        part.style.opacity = 1;
        this.querySelectorAll('[data-follow="'+i+'"]').forEach(surface => {
          surface.style.transform=transform;
          surface.classList.toggle('is-highlighted',selected);
          if(surface.classList.contains('nirun-assembly__tint')) surface.style.opacity=selected ? .48 : 0;
          if(surface.classList.contains('nirun-assembly__edge')){
            surface.style.opacity=selected && (i===2 ? cover===1 : rubberJoin===1) ? 1 : 0;
          }
        });
      });
      const next = this.closest('.shopify-section')?.nextElementSibling;
      const target = next?.querySelector('.feature-image__primary');
      if (this.target && this.target !== target) this.target.style.opacity = '';
      this.target = target;
      if (target?.currentSrc && handoff.src !== target.currentSrc) handoff.src = target.currentSrc;
      const travel = animated && target ? ease((p-.88)/.12) : 0;
      const lift = animated ? Math.min(64, stage.clientHeight * .09) * ease((p-.45)/.20) : 0;
      if (travel > 0) {
        const from = visual.getBoundingClientRect(); const to = target.getBoundingClientRect();
        handoff.style.transform = `translate(${(to.left-from.left)*travel}px,${(to.top-from.top)*travel-lift*(1-travel)}px) scale(${1+(to.width/from.width-1)*travel},${1+(to.height/from.height-1)*travel})`;
      } else handoff.style.transform = `translateY(${-lift}px)`;
      layers.style.transform = handoff.style.transform;
      layers.style.clipPath = 'none';
      // There is no replacement shoe image. Only the photographic background
      // returns once the same assembled layers reach the adjacent product card.
      real.style.opacity = 0; focus.style.opacity = 0;
      const photo = animated ? ease((p-.975)/.025) : 0;
      handoff.style.opacity = animated && target && p >= .999 ? 0 : photo;
      layers.style.opacity = animated && target && p >= .999 ? 0 : 1;
      if (target) target.style.opacity = travel > 0 && p < .999 ? '0' : '';
      this.dataset.progress = p.toFixed(3);
    }
  });
})();


